import { useCallback } from "react";
import { toast } from "sonner";
import { NETWORK_CONFIG, LOGO_URL } from "@/constants/network";
import { tokenLogoUrl } from "@/lib/logos";

const FSC_CHAIN_PARAMS = {
  chainId: NETWORK_CONFIG.chainIdHex,
  chainName: NETWORK_CONFIG.chainName,
  nativeCurrency: NETWORK_CONFIG.nativeCurrency,
  rpcUrls: NETWORK_CONFIG.rpcUrls,
  blockExplorerUrls: NETWORK_CONFIG.blockExplorerUrls,
  iconUrls: [`${window.location.origin}${LOGO_URL}`],
};

/** Adding/switching to FSC, RPC health checks and watchAsset for the connected wallet. */
export function useChainManagement({ providerRef, setChainId }) {
  const activeProvider = useCallback(() => providerRef.current || window.ethereum, [providerRef]);

  // Detects a wallet whose FSC network entry points at a dead/old RPC (e.g. the former rpc.fmichain.io)
  const checkWalletRpc = useCallback(async () => {
    const provider = activeProvider();
    if (!provider) return { ok: false, reason: "no-wallet" };
    const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 6000));
    try {
      const hex = await Promise.race([provider.request({ method: "eth_blockNumber" }), timeout]);
      return { ok: parseInt(hex, 16) > 0 };
    } catch {
      return { ok: false, reason: "rpc-unreachable" };
    }
  }, [activeProvider]);

  const addFSCNetwork = useCallback(async () => {
    const provider = activeProvider();
    if (!provider) {
      toast.error("MetaMask not detected");
      return false;
    }
    const addChain = () => provider.request({ method: "wallet_addEthereumChain", params: [FSC_CHAIN_PARAMS] });
    try {
      try {
        await provider.request({ method: "wallet_switchEthereumChain", params: [{ chainId: NETWORK_CONFIG.chainIdHex }] });
        // An existing entry may still use an old RPC; re-adding lets the wallet pick up the current URL
        if (!(await checkWalletRpc()).ok) await addChain();
      } catch (switchErr) {
        if (switchErr.code === 4001) throw switchErr;
        await addChain();
      }
      setChainId(NETWORK_CONFIG.chainId);
      toast.success("FSC network added and selected");
      return true;
    } catch (err) {
      toast.error(err.code === 4001 ? "Network request rejected" : "Failed to add FSC network");
      return false;
    }
  }, [activeProvider, checkWalletRpc, setChainId]);

  const addUSDTToken = useCallback(async (tokenAddress, decimals, symbol = "USDT", image = tokenLogoUrl(tokenAddress)) => {
    const provider = activeProvider();
    if (!provider || !tokenAddress) return false;
    try {
      return Boolean(await provider.request({
        method: "wallet_watchAsset",
        params: { type: "ERC20", options: { address: tokenAddress, symbol, decimals, image } },
      }));
    } catch {
      return false;
    }
  }, [activeProvider]);

  return { checkWalletRpc, addFSCNetwork, addUSDTToken };
}
